import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ImpersonateAppHandoff from './ImpersonateAppHandoff';

const SPINNER_SX = { display: 'flex', justifyContent: 'center', py: 6 } as const;

// If a direct token fetch resolves but the SDK never flips `isAuthenticated`,
// render the page anyway rather than spin forever — redeem then 401s into a
// message, which is still better than a blank screen.
const SETTLE_TIMEOUT_MS = 3000;

/**
 * Auth gate for the impersonation handoff landing page.
 *
 * We deliberately do NOT reuse CheckLoginComponent here. On Android Chrome its
 * boot-time silent-auth check frequently resolves as "not authenticated" for a
 * user who is in fact signed in, and CheckLoginComponent then renders `null`
 * — a blank screen with no recovery until the tab is killed and the QR
 * re-scanned. This gate only ever renders a spinner or the page:
 *
 *  - while Auth0 is still initialising -> spinner
 *  - `isAuthenticated` -> render the page
 *  - not authenticated -> one direct `getAccessTokenSilently()` (uses the
 *    localStorage refresh token, no iframe); on success the SDK flips
 *    `isAuthenticated` and we render the page, on failure we bounce through a
 *    top-level Auth0 redirect (which picks up any existing SSO session) back
 *    to this same URL.
 */
export default function ImpersonateAppHandoffGate() {
  const { isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [renderPage, setRenderPage] = useState(false);
  const probeStartedRef = useRef(false);

  useEffect(() => {
    if (renderPage || isLoading) return;

    if (isAuthenticated) {
      setRenderPage(true);
      return;
    }

    if (probeStartedRef.current) return;
    probeStartedRef.current = true;

    const safety = setTimeout(() => setRenderPage(true), SETTLE_TIMEOUT_MS);

    getAccessTokenSilently()
      .then(() => {
        // Success: the SDK will flip `isAuthenticated` and this effect re-runs.
        clearTimeout(safety);
      })
      .catch(() => {
        clearTimeout(safety);
        const forwardUrl = `${window.location.pathname}${window.location.search}`;
        loginWithRedirect({
          authorizationParams: {
            audience: process.env.REACT_APP_BACKEND_AUDIENCE,
            scope: 'openid profile email',
            redirect_uri: `${window.location.origin}/forward?forwardUrl=${encodeURIComponent(
              forwardUrl,
            )}`,
          },
        });
      });
  }, [renderPage, isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  if (renderPage) {
    return <ImpersonateAppHandoff />;
  }

  return (
    <Box sx={SPINNER_SX}>
      <CircularProgress />
    </Box>
  );
}
